// ============================================================
// gallery.js — Comportamiento de la galería de generaciones
// ============================================================
import { qs, qsa, on, esc as escapeHtml } from './utils.js';
import { toast } from './toast.js';
import { showConfirmModal } from './subscriptionModal.js';
import { listHistory, deleteGeneration, getJobStatus } from './api.js';

export async function initGallery() {
  const galleries = qsa('.gallery-grid');
  if (!galleries.length) return;

  // Comportamiento de selección, descarga y eliminación
  galleries.forEach((grid) => {
    on(grid, 'click', '.gen-card', (e, card) => {
      // Si el clic es en un botón de acción, no seleccionar
      if (e.target.closest('.gen-card-action')) return;

      qsa('.gen-card', grid).forEach((c) => c.classList.remove('is-selected'));
      card.classList.add('is-selected');
    });

    on(grid, 'click', '[aria-label="Descargar"]', (e, btn) => {
      const url = btn.getAttribute('data-download-url');
      if (url) {
        const a = document.createElement('a');
        a.href = url;
        a.download = url.split('/').pop() || 'generacion';
        a.target = '_blank';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        toast({ message: 'Descarga iniciada', type: 'success' });
      } else {
        toast({ message: 'No hay ningún archivo generado para descargar todavía.', type: 'error' });
      }
    });

    on(grid, 'click', '.btn-del-gen', async (e, btn) => {
      e.stopPropagation();
      const id = btn.getAttribute('data-id');
      if (!id) return;
      
      const confirmed = await showConfirmModal({
        title: '¿Eliminar creación?',
        message: 'Esta creación se eliminará permanentemente de tu galería.',
        confirmText: 'Sí, eliminar',
        cancelText: 'Cancelar'
      });

      if (confirmed) {
        try {
          await deleteGeneration(id);
          const card = btn.closest('.gen-card');
          card?.remove();
          toast({ message: 'Creación eliminada correctamente', type: 'success' });
        } catch (err) {
          console.error('[Gallery] Error al eliminar:', err);
          toast({ message: 'Error al eliminar la creación', type: 'error' });
        }
      }
    });
  });

  // Pre-cargar creaciones recientes en la mesa de trabajo (Imágenes y Vídeo)
  const page = document.body.dataset.page;
  if (page === 'imagenes') {
    await populateWorkBench('#imageGallery', 'image');
  } else if (page === 'video') {
    await populateWorkBench('#videoGallery', 'video');
  }
}

export function createCardElement(item, type) {
  const isPending = (item.status === 'queued' || item.status === 'processing') || (!item.url && item.jobId);
  const card = document.createElement('div');
  card.className = `gen-card ${isPending ? 'is-generating' : 'has-result'} ${type === 'video' ? 'is-video' : ''}`;
  card.style.position = 'relative';

  if (isPending) {
    card.innerHTML = `
      <div class="gen-card-placeholder" style="display: flex; flex-direction: column; align-items: center; justify-content: center; width:100%; height:100%; min-height: 220px; text-align: center; padding: 20px;">
        <div class="spinner-border text-light" role="status" style="width: 2.2rem; height: 2.2rem; border-width: 0.22em; margin-bottom: 14px;"></div>
        <span class="gen-loading-text" style="font-size: 0.88rem; opacity: 0.95; font-weight: 600; color: #fff;">${type === 'video' ? 'Generando vídeo con IA...' : 'Generando...'}</span>
        <span style="font-size: 0.75rem; opacity: 0.65; margin-top: 5px; color: #cbd5e1; max-width: 240px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;">${escapeHtml(item.title || item.prompt || '')}</span>
      </div>
    `;

    if (item.jobId) {
      let attempts = 0;
      const poll = setInterval(async () => {
        attempts++;
        try {
          const res = await getJobStatus(item.jobId);
          if (res.status === 'completed' && res.resultUrl) {
            clearInterval(poll);
            const freshCard = createCardElement({ ...item, url: res.resultUrl, status: 'completed' }, type);
            if (card.parentNode) card.parentNode.replaceChild(freshCard, card);
          } else if (res.status === 'failed' || attempts > 100) {
            clearInterval(poll);
            card.remove();
          }
        } catch (e) {
          if (attempts > 100) {
            clearInterval(poll);
            card.remove();
          }
        }
      }, 3000);
    }
    return card;
  }

  if (type === 'video') {
    let videoSrc = item.url || '';
    if (videoSrc && /\.(png|jpe?g|gif|webp)$/i.test(videoSrc)) {
      videoSrc = '/uploads/demo_cinematic_landscape.mp4';
    }
    const video = document.createElement('video');
    video.src = videoSrc;
    video.controls = true;
    video.playsInline = true;
    video.style.width = '100%';
    video.style.height = '100%';
    video.style.objectFit = 'cover';
    video.style.borderRadius = '12px';
    card.appendChild(video);
  } else {
    const img = document.createElement('img');
    img.src = item.url || item.thumb || '';
    img.alt = item.title || item.prompt || 'Generación Vimo';
    img.style.width = '100%';
    img.style.height = '100%';
    img.style.objectFit = 'cover';
    img.style.borderRadius = '12px';
    card.appendChild(img);
  }

  const actions = document.createElement('div');
  actions.className = 'gen-card-actions';
  actions.innerHTML = `
    <button class="gen-card-action" type="button" aria-label="Descargar" data-download-url="${item.url || ''}">
      <i class="bi bi-download"></i>
    </button>
    <button class="gen-card-action btn-del-gen" type="button" aria-label="Eliminar" data-id="${item.id}">
      <i class="bi bi-trash"></i>
    </button>
  `;
  card.appendChild(actions);
  return card;
}

async function populateWorkBench(gridSelector, type) {
  const grid = qs(gridSelector);
  if (!grid) return;

  grid.innerHTML = ''; // Limpiar cualquier tarjeta previa

  try {
    const items = await listHistory();
    const filteredItems = items.filter(item => item.type === type);

    if (filteredItems.length > 0) {
      filteredItems.forEach(item => {
        const card = createCardElement(item, type);
        grid.appendChild(card);
      });
    } else {
      grid.innerHTML = `
        <div class="gallery-empty-state" style="grid-column: 1 / -1; text-align: center; padding: 50px 20px; color: #94A3B8; background: rgba(255,255,255,0.02); border-radius: 16px; border: 1px dashed rgba(255,255,255,0.1);">
          <i class="bi ${type === 'video' ? 'bi-camera-reels' : 'bi-image'}" style="font-size: 40px; display: block; margin-bottom: 12px; color: #a855f7;"></i>
          <p style="font-size: 16px; font-weight: 700; margin-bottom: 6px; color: #F8FAFC;">Aún no tienes ${type === 'video' ? 'vídeos' : 'imágenes'} creadas</p>
          <span style="font-size: 13.5px; color: #94A3B8;">Escribe tu prompt en el panel inferior para generar tu primera creación.</span>
        </div>
      `;
    }
  } catch (err) {
    console.warn(`[Workbench] No se pudieron cargar los datos del historial para ${type}:`, err);
  }
}
